import { Inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { concatLatestFrom } from '@ngrx/operators'
import { Store } from '@ngrx/store'
import { defer, EMPTY, Observable, of } from 'rxjs'
import { BarterActions } from './action-types'
import { catchError, concatMap, filter, map, mergeMap, switchMap, take } from 'rxjs/operators'
import { ConvertDateUnitService } from '../../../shared/services/convert-date-unit.service'
import { Barter, BarterControllerService } from '../../../api/backend'
import {
    selectAddBarterConcernedParcelsData,
    selectAddBarterCounterpartyParcelsData,
    selectBarter,
    selectBarterCalculateResultData,
    selectBarterConcernedParcels,
    selectBarterConcernedParcelsData,
    selectBarterCounterpartyParcels,
    selectBarterCounterpartyParcelsData,
    selectBarterParcelOrderAndConflicts,
    selectBarterParcelOrderAncConflictsAndPassedParcelGeometries,
    selectBarterParcelOrderAncConflictsAndReceivedParcelGeometries,
    selectBarterUpdateAcreageUsedAcreage,
    selectPassedParcels,
    selectReceivedParcels, selectBarterPassedParcelsData, selectBarterReceivedParcelsData,
} from './barter.selectors'
import { BarterResultService } from '../services/barter-result.service'
import { BarterService } from '../services/barter.service'
import { OrderByService } from '../../../shared/services/order-by.service'
import BARTER_STATE from '../../../constants/barter-state.constant'
import cloneDeep from 'lodash/cloneDeep'
import { BarterParcelTypeEnum } from '../../../constants/barter-parcel-type.constant'
import { ConcernedService } from '../../../domain/subject/services/concerned.service'
import { AuthContextStorageService } from '../../../core/auth/auth-context-storage.service'

@Injectable()
export class BarterEffects {
    setBarter$: Observable<any>
    emptyBarterParcels$: Observable<any>
    calculateBarterResultInternal$: Observable<any>
    prepareBarterConcernedParcelsAndAcreages$: Observable<any>
    prepareBarterCounterpartyParcelsAndAcreages$: Observable<any>
    prepareBarterPassedParcelsAndAcreages$: Observable<any>
    prepareBarterReceivedParcelsAndAcreages$: Observable<any>
    setParcelOrder$: Observable<any>
    prepareConcernedParcels$: Observable<any>
    prepareCounterpartyParcels$: Observable<any>
    preparePassedParcels$: Observable<any>
    prepareReceivedParcels$: Observable<any>
    removeAllParcels$: Observable<any>
    prepareBarterCounterpartyParcelErrorHidden$: Observable<any>
    prepareBarterConcernedParcelErrorHidden$: Observable<any>
    prepareBarterReceivedParcelErrorHidden$: Observable<any>
    prepareBarterPassedParcelErrorHidden$: Observable<any>
    prepareAddBarterConcernedParcels$: Observable<any>
    prepareBarterAutomaticConcernedParcels$: Observable<any>
    setBarterConcernedParcelsAndAcreages$: Observable<any>
    prepareAddBarterCounterpartyParcels$: Observable<any>
    setBarterDurationPeriod$: Observable<any>
    setBarterEfficiencyFromDate$: Observable<any>
    updateAcreageUsedAcreage$: Observable<any>
    handleValidityParams$: Observable<any>
    setBarterReceivedParcelsAndAcreages$: Observable<any>

    private commonErrors = {
        invalidOutdated: 'Parcela je zrušena.',
        parcelOutdated: 'Tato parcela existuje v novější verzi.',
    }
    private errorMessages = {
        barterConcernedParcels: {
            ...this.commonErrors,
            invalidNotUsed: 'K parcele nemáte vyřešen právní důvod užívání.',
            parcelNoFurtherRent: 'Parcela je na platné smlouvě nebo směně, ale nelze ji dále propachtovávat.',
            parcelOutsideLpisBlocks: 'Tato parcela je mimo půdní bloky (LPIS) protistrany, tj. nehospodaří na ní. Ověřte oprávněnost nároku.',
        },
        barterCounterpartyParcels: {
            ...this.commonErrors,
            invalidUsed: 'K parcele máte právní důvod užívaní, což je v rozporu s tím, že ho do směny dává protistrana.',
            parcelOutsideLpisBlocks: 'Tato parcela je mimo vaše půdní bloky (LPIS), tj. nehospodaříte na ní. Ověřte oprávněnost nároku.',
        },
        barterPassedParcels: {
            ...this.commonErrors,
            invalidNotUsed: 'K parcele nemáte vyřešen právní důvod užívání.',
            parcelNoFurtherRent: 'Parcela je na platné smlouvě, ale nelze ji dále propachtovávat.',
            parcelBarterNoFurtherRent: 'Parcela je na platné směně, ale nelze ji dále propachtovávat.',
            parcelOutsideLpisBlocks: 'Tato parcela je mimo vaše půdní bloky (LPIS), tj. nehospodaříte na ní. Ověřte oprávněnost nároku.',
        },
        barterReceivedParcel: {
            ...this.commonErrors,
            invalidUsed: 'K parcele máte právní důvod užívaní, což je v rozporu s tím, že ho do směny dává protistrana.',
            parcelOutsideLpisBlocks: 'Tato parcela je mimo půdní bloky (LPIS) protistrany, tj. nehospodaří na ní. Ověřte oprávněnost nároku.',
        },
    }

    constructor(
        @Inject(Actions) private actions$: Actions,
        @Inject(Store) private store: Store,
        @Inject(OrderByService) private orderByService: OrderByService,
        @Inject(AuthContextStorageService) private authContextStorageService: AuthContextStorageService,
        @Inject(BarterControllerService) private barterControllerService: BarterControllerService,
        @Inject(BarterService) private barterService: BarterService,
        @Inject(BarterResultService) private barterResultService: BarterResultService,
        @Inject(ConvertDateUnitService) private convertDateUnitService: ConvertDateUnitService,
        @Inject(ConcernedService) private concernedService: ConcernedService,
    ) {
        this.setBarter$ = createEffect(() => {

            return this.actions$.pipe(
                ofType(BarterActions.setBarter),
                switchMap(action =>
                    this.computeExpirationDate(this.authContextStorageService.getProjectKey(), action.barter as Barter, action.computeExpirationDate).pipe(
                        map(result => {
                            return action.computeExpirationDate ? BarterActions.setBarterExpirationDate({ date: result }) : BarterActions.noAction()
                        }))),
            )
        })

        this.emptyBarterParcels$ = createEffect(() => {
                return this.actions$.pipe(
                    ofType(BarterActions.emptyBarterParcels),
                    switchMap(() => this.calculateBarterResultInternal().pipe(
                        map(result => {
                            return result ? BarterActions.setBarterResult({ result: this.barterResultService.calculateBarterResult(result.data, result.lpisKulturas) }) : BarterActions.setBarterResult(null)
                        })),
                    ))
            },
        )

        this.calculateBarterResultInternal$ = createEffect(() => {
                return this.actions$.pipe(
                    ofType(BarterActions.calculateBarterResultInternal),
                    switchMap(() => this.calculateBarterResultInternal().pipe(
                        map(result => {
                            return result ? BarterActions.setBarterResult({ result: this.barterResultService.calculateBarterResult(result.data, result.lpisKulturas) }) : BarterActions.setBarterResult(null)
                        })),
                    ))
            },
        )

        this.prepareBarterConcernedParcelsAndAcreages$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareBarterConcernedParcelsAndAcreages),
                concatLatestFrom(() => this.store.select(selectBarterParcelOrderAndConflicts)),
                map(([action, { parcelOrder, parcelConflicts }]) => {
                    const barterParcels = this.orderParcels(this.updateBarterParcelsWithCalculatedColumns(action.parcels, action.acreages), parcelOrder)
                    this.enhanceWitchErrors(barterParcels, parcelConflicts, 'barterConcernedParcels', 'concerned')
                    /**
                     * You need to explicitly call this.calculateBarterResult after this method. Its not call inside on purpose.
                     * It is expected you have conflicts and ownerships already saved in global state.
                     */
                    return BarterActions.setBarterConcernedParcelsAndAcreages({
                        parcels: barterParcels,
                        parcelIds: this.barterService.getBarterParcelIds(barterParcels),
                        acreages: action.acreages,
                        sum: this.barterService.calculateBarterAcreagesSum(action.acreages),
                        calculate: action.calculate
                    })
                }),
            )
        })

        this.prepareBarterCounterpartyParcelsAndAcreages$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareBarterCounterpartyParcelsAndAcreages),
                concatLatestFrom(() => this.store.select(selectBarterParcelOrderAndConflicts)),
                map(([action, { parcelOrder, parcelConflicts }]) => {
                    const barterParcels = this.orderParcels(this.updateBarterParcelsWithCalculatedColumns(action.parcels, action.acreages), parcelOrder)
                    this.enhanceWitchErrors(barterParcels, parcelConflicts, 'barterCounterpartyParcels', 'counterparty')
                    /**
                     * You need to explicitly call this.calculateBarterResult after this method. Its not call inside on purpose.
                     * It is expected you have conflicts and ownerships already saved in global state.
                     */
                    return BarterActions.setBarterCounterpartyParcelsAndAcreages({
                        parcels: barterParcels,
                        parcelIds: this.barterService.getBarterParcelIds(barterParcels),
                        acreages: action.acreages,
                        sum: this.barterService.calculateBarterAcreagesSum(action.acreages),
                        calculate: action.calculate
                    })
                }),
            )
        })

        this.prepareBarterPassedParcelsAndAcreages$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareBarterPassedParcelsAndAcreages),
                concatLatestFrom(() => this.store.select(selectBarterParcelOrderAncConflictsAndPassedParcelGeometries)),
                map(([action, { parcelOrder, parcelConflicts, passedParcelGeometries }]) => {
                    const passedParcelGeometriesCopy = cloneDeep(passedParcelGeometries)
                    action.parcels.flatMap(parcel => parcel.parcels).forEach(selectedParcel => {
                        selectedParcel.stgImportGeomParcelGeometries.forEach(addedGeometryRaw => {
                            const addedGeom = {
                                parcel: selectedParcel.parcel,
                                parcelId: selectedParcel.parcelId,
                                parId: selectedParcel.parId,
                                id: addedGeometryRaw.id,
                                geom: addedGeometryRaw.geom,
                            }
                            this.barterService.addParcelGeometryToBarterParcelGeometries(passedParcelGeometriesCopy, addedGeom)
                        })
                    })

                    const barterParcels = this.orderParcels(this.updateBarterParcelsWithCalculatedColumns(action.parcels, action.acreages), parcelOrder)
                    this.enhanceWitchErrors(barterParcels, parcelConflicts, 'barterPassedParcels', 'passed')
                    /**
                     * You need to explicitly call this.calculateBarterResult after this method. Its not call inside on purpose.
                     * It is expected you have conflicts and ownerships already saved in global state.
                     */
                    return BarterActions.setBarterPassedParcelsAndAcreages({
                        parcels: barterParcels,
                        parcelIds: this.barterService.getBarterParcelIds(barterParcels),
                        acreages: action.acreages,
                        sum: this.barterService.calculateBarterAcreagesSum(action.acreages),
                        passedParcelGeometries: passedParcelGeometriesCopy,
                        calculate: action.calculate
                    })
                }),
            )
        })

        this.prepareBarterReceivedParcelsAndAcreages$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareBarterReceivedParcelsAndAcreages),
                concatLatestFrom(() => this.store.select(selectBarterParcelOrderAncConflictsAndReceivedParcelGeometries)),
                map(([action, { parcelOrder, parcelConflicts, receivedParcelGeometries }]) => {
                    const receivedParcelGeometriesCopy = cloneDeep(receivedParcelGeometries)
                    action.parcels.flatMap(parcel => parcel.parcels).forEach(selectedParcel => {
                        selectedParcel.stgImportGeomParcelGeometries.forEach(addedGeometryRaw => {
                            const addedGeom = {
                                parcel: selectedParcel.parcel,
                                parcelId: selectedParcel.parcelId,
                                parId: selectedParcel.parId,
                                id: addedGeometryRaw.id,
                                geom: addedGeometryRaw.geom,
                            }
                            this.barterService.addParcelGeometryToBarterParcelGeometries(receivedParcelGeometriesCopy, addedGeom)
                        })
                    })

                    const barterParcels = this.orderParcels(this.updateBarterParcelsWithCalculatedColumns(action.parcels, action.acreages), parcelOrder)
                    this.enhanceWitchErrors(barterParcels, parcelConflicts, 'barterReceivedParcel', 'received')
                    /**
                     * You need to explicitly call this.calculateBarterResult after this method. Its not call inside on purpose.
                     * It is expected you have conflicts and ownerships already saved in global state.
                     */
                    return BarterActions.setBarterReceivedParcelsAndAcreages({
                        parcels: barterParcels,
                        parcelIds: this.barterService.getBarterParcelIds(barterParcels),
                        acreages: action.acreages,
                        sum: this.barterService.calculateBarterAcreagesSum(action.acreages),
                        receivedParcelGeometries: receivedParcelGeometriesCopy,
                        calculate: action.calculate
                    })
                }),
            )
        })

        this.setBarterReceivedParcelsAndAcreages$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.setBarterReceivedParcelsAndAcreages,
                    BarterActions.setBarterPassedParcelsAndAcreages,
                    BarterActions.setBarterCounterpartyParcelsAndAcreages,
                    BarterActions.setBarterConcernedParcelsAndAcreages),
                filter(action => action.calculate),
                map(() => BarterActions.calculateBarterResultInternal()),
            )
        })


        this.setParcelOrder$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.setParcelOrder),
                mergeMap(() => [
                    BarterActions.prepareConcernedParcels(),
                    BarterActions.prepareCounterpartyParcels(),
                    BarterActions.preparePassedParcels(),
                    BarterActions.prepareReceivedParcels()
                ]),
            )
        })

        this.prepareConcernedParcels$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareConcernedParcels),
                concatLatestFrom(() => this.store.select(selectBarterConcernedParcelsData)),
                map(([, { concernedParcels, concernedParcelAcreages, parcelOrder }]) => {
                    return BarterActions.setBarterConcernedParcels({
                        concernedParcels: this.orderParcels(this.updateBarterParcelsWithCalculatedColumns(concernedParcels, concernedParcelAcreages), parcelOrder),
                        concernedParcelIds: this.barterService.getBarterParcelIds(concernedParcels),
                    })
                }),
            )
        })

        this.prepareCounterpartyParcels$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareCounterpartyParcels),
                concatLatestFrom(() => this.store.select(selectBarterCounterpartyParcelsData)),
                map(([, { counterpartyParcels, counterpartyParcelAcreages, parcelOrder }]) => {
                    return BarterActions.setCounterpartyParcels({
                        counterpartyParcels: this.orderParcels(this.updateBarterParcelsWithCalculatedColumns(counterpartyParcels, counterpartyParcelAcreages), parcelOrder),
                        counterpartyParcelIds: this.barterService.getBarterParcelIds(counterpartyParcels),
                    })
                }),
            )
        })

        this.preparePassedParcels$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.preparePassedParcels),
                concatLatestFrom(() => this.store.select(selectBarterPassedParcelsData)),
                map(([, { passedParcels, passedParcelAcreages, parcelOrder }]) => {
                    return BarterActions.setPassedParcels({
                        passedParcels: this.orderParcels(this.updateBarterParcelsWithCalculatedColumns(passedParcels, passedParcelAcreages), parcelOrder),
                        passedParcelsIds: this.barterService.getBarterParcelIds(passedParcels),
                    })
                }),
            )
        })

        this.prepareReceivedParcels$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareReceivedParcels),
                concatLatestFrom(() => this.store.select(selectBarterReceivedParcelsData)),
                map(([, { receivedParcels, receivedParcelAcreages, parcelOrder }]) => {
                    return BarterActions.setReceivedParcels({
                        receivedParcels: this.orderParcels(this.updateBarterParcelsWithCalculatedColumns(receivedParcels, receivedParcelAcreages), parcelOrder),
                        receivedParcelsIds: this.barterService.getBarterParcelIds(receivedParcels),
                    })
                }),
            )
        })

        this.removeAllParcels$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.removeAllParcels),
                concatMap(({ barterParcelType }) => {
                    switch (barterParcelType) {
                        case BarterParcelTypeEnum.concerned:
                            return [BarterActions.prepareBarterConcernedParcelsAndAcreages({ parcels: [], acreages: [], calculate: true }),
                                BarterActions.setBarterIsAnyConcernedOrCounterpartyParcelUpdated({ value: true }),
                            ]
                        case BarterParcelTypeEnum.counterparty:
                            return [BarterActions.prepareBarterCounterpartyParcelsAndAcreages({ parcels: [], acreages: [], calculate: true }),
                                BarterActions.setBarterIsAnyConcernedOrCounterpartyParcelUpdated({ value: true }),
                            ]
                        case BarterParcelTypeEnum.passed:
                            return [
                                BarterActions.setBarterPassedGeometries({ geometries: [] }),
                                BarterActions.prepareBarterPassedParcelsAndAcreages({ parcels: [], acreages: [], calculate: true }),
                            ]
                        case BarterParcelTypeEnum.received:
                            return [
                                BarterActions.setBarterReceivedGeometries({ geometries: [] }),
                                BarterActions.prepareBarterReceivedParcelsAndAcreages({ parcels: [], acreages: [], calculate: true }),
                            ]
                    }
                }),
            )
        })

        this.prepareBarterCounterpartyParcelErrorHidden$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareBarterCounterpartyParcelErrorHidden),
                concatLatestFrom(() => this.store.select(selectBarterCounterpartyParcels)),
                map(([{ parcelId, value }, counterpartyParcels]) => {
                    const updatedCounterpartyParcels = cloneDeep(counterpartyParcels)
                    this.updateParcelGroupWithErrors(updatedCounterpartyParcels, parcelId, value)
                    // counterpartyParcels.some(barterParcelGroup => {
                    //     const barterParcel = barterParcelGroup.parcels.find(p => p.parcelId === parcelId)
                    //     if (barterParcel) {
                    //         barterParcel.errorHidden = value
                    //         barterParcelGroup.iconWarning = this.buildGroupIconWarning(barterParcelGroup.parcels)
                    //         return true  // This will break the loop
                    //     }
                    //     return false  // Continue to next iteration
                    // })
                    return BarterActions.setBarterCounterpartyParcelErrorHidden({
                        barterParcelGroups: updatedCounterpartyParcels,
                        barterParcelGroupIds: this.barterService.getBarterParcelIds(updatedCounterpartyParcels),
                    })
                }),
            )
        })

        this.prepareBarterConcernedParcelErrorHidden$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareBarterConcernedParcelErrorHidden),
                concatLatestFrom(() => this.store.select(selectBarterConcernedParcels)),
                map(([{ parcelId, value }, concernedParcels]) => {
                    const updatedConcernedParcels = cloneDeep(concernedParcels)
                    this.updateParcelGroupWithErrors(updatedConcernedParcels, parcelId, value)
                    return BarterActions.setBarterConcernedParcelErrorHidden({
                        barterParcelGroups: updatedConcernedParcels,
                        barterParcelGroupIds: this.barterService.getBarterParcelIds(updatedConcernedParcels),
                    })
                }),
            )
        })

        this.prepareBarterReceivedParcelErrorHidden$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareBarterReceivedParcelErrorHidden),
                concatLatestFrom(() => this.store.select(selectReceivedParcels)),
                map(([{ parcelId, value }, receivedParcels]) => {
                    const updatedReceivedParcels = cloneDeep(receivedParcels)
                    this.updateParcelGroupWithErrors(updatedReceivedParcels, parcelId, value)
                    return BarterActions.setBarterReceivedParcelErrorHidden({
                        barterParcelGroups: updatedReceivedParcels,
                        barterParcelGroupIds: this.barterService.getBarterParcelIds(updatedReceivedParcels),
                    })
                }),
            )
        })

        this.prepareBarterPassedParcelErrorHidden$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareBarterPassedParcelErrorHidden),
                concatLatestFrom(() => this.store.select(selectPassedParcels)),
                map(([{ parcelId, value }, passedParcels]) => {
                    const updatedPassedParcels = cloneDeep(passedParcels)
                    this.updateParcelGroupWithErrors(updatedPassedParcels, parcelId, value)
                    return BarterActions.setBarterPassedParcelErrorHidden({
                        barterParcelGroups: updatedPassedParcels,
                        barterParcelGroupIds: this.barterService.getBarterParcelIds(updatedPassedParcels),
                    })
                }),
            )
        })

        this.prepareAddBarterConcernedParcels$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareAddBarterConcernedParcels),
                concatLatestFrom(() => this.store.select(selectAddBarterConcernedParcelsData)),
                concatMap(([{ selectedParcels, addedAcreages, validityParams }, {
                    barterId,
                    barterCounterpartyId,
                    concernedParcels,
                    concernedParcelAcreages,
                    concernedSubject,
                    counterparty,
                    parcelConflicts,
                }]) => {
                    const concernedParcelsCopy = cloneDeep(concernedParcels)
                    const concernedParcelAcreagesCopy = cloneDeep(concernedParcelAcreages)
                    const updatedAddedAcreage = cloneDeep(addedAcreages)
                    selectedParcels.forEach(selectedParcel => {
                        this.barterService.addParcelToBarterParcels(concernedParcelsCopy, selectedParcel, concernedParcelAcreagesCopy, barterId)
                    })
                    updatedAddedAcreage.forEach(addedAcreage => {
                        this.barterService.addParcelAcreageToBarterParcelAcreages(concernedParcelAcreagesCopy, addedAcreage, BarterParcelTypeEnum.concerned, concernedSubject, counterparty)
                    })
                    this.barterService.normalizeParcelAcreages(
                        concernedParcelAcreagesCopy,
                        concernedParcelsCopy,
                        this.barterService.getContextLpisUser(BarterParcelTypeEnum.concerned, concernedSubject, counterparty),
                        updatedAddedAcreage)
                    this.calculateDynamicValidity(BarterParcelTypeEnum.concerned, concernedParcelsCopy, concernedParcelAcreagesCopy,
                        parcelConflicts, this.concernedService.getAllConcerned(), barterCounterpartyId, selectedParcels, validityParams)
                    this.enhanceWitchErrors(concernedParcelsCopy, parcelConflicts, 'barterConcernedParcels', 'concerned')

                    return [BarterActions.setBarterConcernedParcelsAndAcreages({
                        parcels: concernedParcelsCopy,
                        parcelIds: this.barterService.getBarterParcelIds(concernedParcelsCopy),
                        acreages: concernedParcelAcreagesCopy,
                        sum: this.barterService.calculateBarterAcreagesSum(concernedParcelAcreagesCopy),
                    }),
                        BarterActions.prepareConcernedParcels(),
                        BarterActions.calculateBarterResultInternal()]
                }),
            )
        })

        this.prepareBarterAutomaticConcernedParcels$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareBarterAutomaticConcernedParcels),
                concatLatestFrom(() => this.store.select(selectAddBarterConcernedParcelsData)),
                switchMap(([{ selectedParcels, validityParams }, {
                    barterId,
                    barterCounterpartyId,
                    concernedParcels,
                    concernedParcelAcreages,
                    concernedSubject,
                    counterparty,
                    parcelConflicts,
                }]) => {
                    let concernedParcelsCopy = cloneDeep(concernedParcels)
                    let concernedParcelAcreagesCopy = cloneDeep(concernedParcelAcreages)
                    //both independent cycles of selectedParcels are neccessary because acrages are cleaned in the first one
                    selectedParcels.forEach(selectedParcel => {
                        this.barterService.addParcelToBarterParcels(
                            concernedParcelsCopy, selectedParcel.parcel, concernedParcelAcreagesCopy, barterId)
                    })
                    selectedParcels.forEach(selectedParcel => {
                        this.barterService.addParcelAcreageToBarterParcelAcreages(
                            concernedParcelAcreagesCopy, selectedParcel, BarterParcelTypeEnum.concerned, concernedSubject,
                            counterparty)
                    })
                    this.barterService.normalizeParcelAcreages(
                        concernedParcelAcreagesCopy,
                        concernedParcelsCopy,
                        this.barterService.getContextLpisUser(BarterParcelTypeEnum.concerned, concernedSubject, counterparty))
                    const concernedSubjects = this.concernedService.getAllConcerned()
                    this.calculateDynamicValidity(BarterParcelTypeEnum.concerned, concernedParcelsCopy, concernedParcelAcreagesCopy,
                        parcelConflicts, concernedSubjects, barterCounterpartyId, selectedParcels, validityParams)
                    this.enhanceWitchErrors(concernedParcelsCopy, parcelConflicts, 'barterConcernedParcels', 'concerned')

                    return [BarterActions.setBarterConcernedParcelsAndAcreages({
                        parcels: concernedParcelsCopy,
                        parcelIds: this.barterService.getBarterParcelIds(concernedParcelsCopy),
                        acreages: concernedParcelAcreagesCopy,
                        sum: this.barterService.calculateBarterAcreagesSum(concernedParcelAcreagesCopy),
                    }),
                        BarterActions.prepareConcernedParcels(),
                        BarterActions.calculateBarterResultInternal()]
                }),
            )
        })

        this.prepareAddBarterCounterpartyParcels$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.prepareAddBarterCounterpartyParcels),
                //selectAddBarterCounterpartyParcelsData
                concatLatestFrom(() => this.store.select(selectAddBarterCounterpartyParcelsData)),
                concatMap(([{
                    selectedParcels,
                    addedAcreages,
                    validityParams,
                }, {
                    barterId,
                    counterpartyParcels,
                    counterpartyParcelAcreages,
                    concernedSubject,
                    counterparty,
                    parcelConflicts,
                }]) => {
                    let counterpartyParcelsCopy = cloneDeep(counterpartyParcels)
                    let counterpartyParcelAcreagesCopy = cloneDeep(counterpartyParcelAcreages)
                    const updatedAddedAcreage = cloneDeep(addedAcreages)
                    selectedParcels.forEach(selectedParcel => {
                        this.barterService.addParcelToBarterParcels(counterpartyParcelsCopy, selectedParcel, counterpartyParcelAcreagesCopy, barterId)
                    })
                    updatedAddedAcreage.forEach(acreage => {
                        this.barterService.addParcelAcreageToBarterParcelAcreages(
                            counterpartyParcelAcreagesCopy, acreage, BarterParcelTypeEnum.counterparty, concernedSubject,
                            counterparty)
                    })
                    this.barterService.normalizeParcelAcreages(
                        counterpartyParcelAcreagesCopy,
                        counterpartyParcelsCopy,
                        this.barterService.getContextLpisUser(BarterParcelTypeEnum.counterparty, concernedSubject, counterparty),
                        updatedAddedAcreage)
                    const concernedSubjects = this.concernedService.getAllConcerned()
                    this.calculateDynamicValidity(BarterParcelTypeEnum.counterparty, counterpartyParcelsCopy,
                        counterpartyParcelAcreagesCopy, parcelConflicts, concernedSubjects, counterparty.id, selectedParcels, validityParams)
                    this.enhanceWitchErrors(counterpartyParcelsCopy, parcelConflicts, 'barterCounterpartyParcels', 'counterparty')

                    return [BarterActions.setBarterCounterpartyParcelsAndAcreages({
                        parcels: counterpartyParcelsCopy,
                        parcelIds: this.barterService.getBarterParcelIds(counterpartyParcelsCopy),
                        acreages: counterpartyParcelAcreagesCopy,
                        sum: this.barterService.calculateBarterAcreagesSum(counterpartyParcelAcreagesCopy),
                    }),
                        BarterActions.prepareCounterpartyParcels(),
                        BarterActions.calculateBarterResultInternal(),
                    ]
                }),
            )
        })

        this.setBarterDurationPeriod$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.setBarterDurationMonths, BarterActions.setBarterDurationMonthsUnit, BarterActions.setBarterDurationPeriod, BarterActions.setBarterDurationToDate),
                concatLatestFrom(() => this.store.select(selectBarter)),
                switchMap(([, barter]) => this.computeExpirationDate(this.authContextStorageService.getProjectKey(), barter as Barter).pipe(
                    map(result => {
                        return BarterActions.setBarterExpirationDate({ date: result })
                    }),
                )),
            )
        })

        this.setBarterEfficiencyFromDate$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.setBarterEfficiencyFromDate, BarterActions.setBarterDurationMonthsUnit, BarterActions.setBarterDurationPeriod, BarterActions.setBarterDurationToDate),
                concatLatestFrom(() => this.store.select(selectBarter)),
                switchMap(([, barter]) => this.computeExpirationDate(this.authContextStorageService.getProjectKey(), barter as Barter).pipe(
                    map(result => {
                        return BarterActions.setBarterExpirationDate({ date: result })
                    })),
                ),
            )
        })

        this.updateAcreageUsedAcreage$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.updateAcreageUsedAcreage),
                concatLatestFrom(() => this.store.select(selectBarterUpdateAcreageUsedAcreage)),
                switchMap(([{ parcelType, barterParcel, updatedAcreage }, data]) => {
                    const updatedData = cloneDeep(data)
                    if (parcelType === BarterParcelTypeEnum.concerned) {
                        const acreages = updatedData.concernedParcelAcreages.map(sa => sa.parcelId === barterParcel.parcelId && sa.lpisKultura.id === barterParcel.lpisKultura.id ?
                            { ...sa, acreage: updatedAcreage } : sa)
                        return [
                            BarterActions.setBarterConcernedParcelAcreages({ acreages: acreages }),
                            BarterActions.setBarterConcernedParcelAcreageSum({ sum: this.barterService.calculateBarterAcreagesSum(acreages) }),
                            BarterActions.handleValidityParams({ parcelType, barterParcel }),
                        ]
                    } else if (parcelType === BarterParcelTypeEnum.counterparty) {
                        const acreages = updatedData.counterpartyParcelAcreages.map(sa => sa.parcelId === barterParcel.parcelId && sa.lpisKultura.id === barterParcel.lpisKultura.id ?
                            Object.assign(sa, { acreage: updatedAcreage }) : sa)
                        return [
                            BarterActions.setBarterCounterpartyParcelAcreages({ acreages: acreages, sum: this.barterService.calculateBarterAcreagesSum(acreages) }),
                            // BarterActions.setBarterCounterpartyParcelAcreageSum({ sum: this.barterService.calculateBarterAcreagesSum(acreages) }),
                            BarterActions.handleValidityParams({ parcelType, barterParcel })]
                    }
                }),
            )
        })

        this.handleValidityParams$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterActions.handleValidityParams),
                switchMap(({ parcelType, barterParcel }) => this.store.select(selectBarterUpdateAcreageUsedAcreage).pipe(
                    take(1),
                    switchMap((state) => this.barterControllerService.getValidityParams(
                        this.authContextStorageService.getProjectKey(),
                        {
                            parcelId: [barterParcel.parcelId],
                            concernedSubjectId: state.concernedSubject.id,
                            excludedBarterGroupId: state.barterGroupId,
                        },
                    ).pipe(map(validityParamsRaw => ({ state, barterParcel, validityParamsRaw })),
                        catchError(error => {
                            console.warn('handleValidityParams$ failed:', error?.status)
                            return EMPTY
                        })
                    )),
                    concatMap(({ state, barterParcel, validityParamsRaw }) => {
                        const validityParams = validityParamsRaw?.items
                        const concernedSubjects = this.concernedService.getAllConcerned()
                        if (parcelType === BarterParcelTypeEnum.concerned) {
                            const concernedParcelsCopy: any[] = cloneDeep(state.concernedParcels)
                            this.resetBarterParcelErrors(concernedParcelsCopy, barterParcel.parcelId)
                            this.calculateDynamicValidity(BarterParcelTypeEnum.concerned, concernedParcelsCopy, state.concernedParcelAcreages,
                                state.parcelConflicts, concernedSubjects, state.counterparty.id, [barterParcel], validityParams)
                            this.enhanceWitchErrors(concernedParcelsCopy, state.parcelConflicts, 'barterConcernedParcels', 'concerned')
                            return [
                                BarterActions.setBarterConcernedParcels({
                                    concernedParcels: concernedParcelsCopy,
                                    concernedParcelIds: this.barterService.getBarterParcelIds(concernedParcelsCopy),
                                }),
                                BarterActions.prepareConcernedParcels(),
                                BarterActions.calculateBarterResultInternal(),
                            ]
                        } else if (parcelType === BarterParcelTypeEnum.counterparty) {
                            const counterpartyParcelsCopy: any[] = cloneDeep(state.counterpartyParcels)
                            this.resetBarterParcelErrors(counterpartyParcelsCopy, barterParcel.parcelId)
                            this.calculateDynamicValidity(BarterParcelTypeEnum.counterparty, counterpartyParcelsCopy, state.counterpartyParcelAcreages,
                                state.parcelConflicts, concernedSubjects, state.counterparty.id, [barterParcel], validityParams)
                            this.enhanceWitchErrors(counterpartyParcelsCopy, state.parcelConflicts, 'barterCounterpartyParcels', 'counterparty')
                            return [BarterActions.setBarterCounterpartyParcelsAndAcreages({
                                parcels: counterpartyParcelsCopy,
                                parcelIds: this.barterService.getBarterParcelIds(counterpartyParcelsCopy),
                                acreages: state.counterpartyParcelAcreages,
                                sum: this.barterService.calculateBarterAcreagesSum(state.counterpartyParcelAcreages)
                            }),BarterActions.prepareCounterpartyParcels(), BarterActions.calculateBarterResultInternal()]
                        }
                        // return BarterActions.setBarterConcernedParcels({concernedParcels: state.concernedParcels, concernedParcelIds: this.barterService.getBarterParcelIds(state.concernedParcels)})
                        // return BarterActions.setBarterCounterpartyParcels({counterpartyParcels: state.counterpartyParcels, counterpartyParcelIds: this.barterService.getBarterParcelIds(state.counterpartyParcels)})
                        // return [BarterActions.prepareConcernedParcels(), BarterActions.calculateBarterResultInternal()]
                    }),
                    // return BarterActions.handleValidityParamsInternal(data)
                )),
            )
        })
    }

    private computeExpirationDate(projectKey: string, data: Barter, computeExpirationDate: boolean = true): Observable<any> {
        if (!computeExpirationDate) {
            return of(null)
        }
        // Bez data účinnosti a délky trvání backend výpočet odmítne (400), rozepsaný formulář proto neposíláme
        if (!this.canComputeExpirationDate(data)) {
            return EMPTY
        }
        // Když backend výpočet odmítne (400), datum konce zůstane beze změny; chyba nesmí zabít effect
        return defer(() => this.barterControllerService.computeBarterExpirationDateFromRequestDto(projectKey, this.convertDateUnitService.cloneAndConvertBarterDateUnitsToMonths(data))).pipe(
            catchError(() => EMPTY),
        )
    }

    private canComputeExpirationDate(data: Barter): boolean {
        return !!data?.efficiencyFromDate && (!!data.indefinite || data.durationMonths > 0 || !!data.durationToDate)
    }

    private calculateBarterResultInternal(): Observable<any> {
        return this.store.select(selectBarterCalculateResultData).pipe(
            take(1),
            switchMap((data) => {
            if (data.lpisKulturasState.lpisKulturas && (
                (data.stateConcernedParcelIds && data.stateConcernedParcelAcreages) ||
                (data.stateCounterpartyParcelIds && data.stateCounterpartyParcelAcreages) ||
                (data.statePassedParcelIds && data.statePassedParcelAcreages) ||
                (data.stateReceivedParcelIds && data.stateReceivedParcelAcreages))
            ) {
                return of({ data, lpisKulturas: data.lpisKulturasState.lpisKulturas })
            } else {
                return of(null)
            }
        }))
    }

    private orderParcels(parcelGroups, parcelsOrder): any {
        let parcelsOrderBy = [], groupsOrderBy = []

        for (const parcelOrder of parcelsOrder) {
            const order = parcelOrder.sortedBy

            if (order === 'parcis') {
                parcelsOrderBy.push('parcel.parcisKmen', 'parcel.parcisPod', 'parcel.parcisDil')
            } else if (order === 'katuzeLv') {
                groupsOrderBy.push('katuzeNazev', 'lv')
            } else if (order === 'lv') {
                groupsOrderBy.push('lv')
            } else {
                parcelsOrderBy.push(order)
            }
        }

        for (const group of parcelGroups) {
            group.parcels = this.orderByService.orderBy(group.parcels, parcelsOrderBy, parcelsOrder[0].desc)
        }

        return groupsOrderBy.length > 0
            ? this.orderByService.orderBy(parcelGroups, groupsOrderBy, parcelsOrder[0].desc)
            : parcelGroups
    }

    private updateBarterParcelsWithCalculatedColumns(parcels, parcelAcreages): any[] {
        const updatedParcels = []
        const clonedParcels = cloneDeep(parcels)

        for (const group of clonedParcels) {
            for (const item of group.parcels) {
                let itemAcreages = this.barterService.getBarterParcelAcreagesForParcel(item, parcelAcreages)
                item.calculatedLandType = this.barterService.getBarterParcelAcreagesKulturaNames(itemAcreages)
                item.calculatedAcreage = this.barterService.getBarterParcelAcreagesSum(itemAcreages)
            }

            group.acreage = group.parcels.reduce((sum, parcel: any) => sum + parcel.calculatedAcreage, 0)
            group.katuzeNazev = group.parcels[0].parcel.katuzeNazev
            updatedParcels.push(group)
        }

        return updatedParcels
    }

    private enhanceWitchErrors(barterParcelGroups, parcelConflicts, enhanceKey: string, conflictsKey: string): void {
        for (const barterParcelGroup of barterParcelGroups) {
            for (const barterParcel of barterParcelGroup.parcels) {
                if (!barterParcel.errors) {
                    barterParcel.errors = []
                }
                for (const error in this.errorMessages[enhanceKey]) {
                    if (barterParcel[error]) {
                        this.addError(barterParcel.errors, this.errorMessages[enhanceKey][error])
                    }
                }

                const barterParcelConflicts = parcelConflicts[conflictsKey]?.[barterParcel.parcelId]
                if ((barterParcel.parcelOnOtherProposalBarter || barterParcel.parcelOnOtherValidBarter) && barterParcelConflicts) {
                    for (const conflict of barterParcelConflicts) {
                        this.addError(barterParcel.errors, 'Tato parcela je již zahrnuta '
                            + (conflict.state === BARTER_STATE.proposal ? 've směně v návrhu' : 'v platné směně')
                            + ' č. '
                            + conflict.barterNumberOfficial
                            + '. Vyřešte konflikt směn.')
                    }
                }
            }
            barterParcelGroup.iconWarning = this.buildGroupIconWarning(barterParcelGroup.parcels)
        }
    }

    private buildGroupIconWarning(barterParcels): string {
        const cntWar = barterParcels.reduce((sum, parcel) => {
            return sum + ((parcel.errors || []).length > 0 ? 1 : 0)
        }, 0)
        const cntWarHidden = barterParcels.reduce((sum, parcel) => {
            return sum + ((parcel.errors || []).length > 0 && parcel.errorHidden ? 1 : 0)
        }, 0)
        return cntWar > 0 ? (cntWarHidden === cntWar ? 'icon-warning-inactive' : 'icon-warning') : null
    }

    private addError(errors, value): void {
        if (!errors.includes(value)) {
            errors.push(value)
        }
    }

    private updateParcelGroupWithErrors(parcels: any[], parcelId: number, value: boolean): void {
        parcels.some(barterParcelGroup => {
            const barterParcel = barterParcelGroup.parcels.find(p => p.parcelId === parcelId)
            if (barterParcel) {
                barterParcel.errorHidden = value
                barterParcelGroup.iconWarning = this.buildGroupIconWarning(barterParcelGroup.parcels)
                return true  // This will break the loop
            }
            return false  // Continue to next iteration
        })
    }

    private calculateDynamicValidity(type: BarterParcelTypeEnum, barterParcelGroups: any[], barterParcelAcreages, parcelConflicts, concernedSubjects, barterCounterpartyId, selectedParcels, validityParams): void {
        barterParcelGroups.forEach(barterParcelGroup => {
            const onlySelectedBarterParcels = barterParcelGroup.parcels.filter(bpgp =>
                selectedParcels.some(sp => (sp.parcel && sp.parcelId === bpgp.parcelId) || sp.id === bpgp.parcelId),
            )
            onlySelectedBarterParcels.forEach(barterParcel => {
                const relevantValidityParams = validityParams.find(validityParam => (validityParam.parcelId === barterParcel.parcelId)) ||
                    {
                        concernedAcreage: 0,
                        parcelRentedAcreagePlus: 0,
                        barterRentedAcreagePlus: 0,
                        parcelRentedAcreageMinus: 0,
                        barterRentedAcreageMinus: 0,
                    }
                const realAcreage = Math.max(barterParcel.parcel.vymera, barterParcel.parcel.parcelLpisIntersect.acreageGeom)
                const rentedAcreage = Math.min(relevantValidityParams.concernedAcreage + relevantValidityParams.parcelRentedAcreagePlus + relevantValidityParams.barterRentedAcreagePlus, realAcreage)
                const unrentedAcreage = Math.min(relevantValidityParams.parcelRentedAcreageMinus + relevantValidityParams.barterRentedAcreageMinus, realAcreage)
                const acreage = this.barterService.getBarterParcelAcreagesSum(this.barterService.getBarterParcelAcreagesForParcel(barterParcel, barterParcelAcreages))

                if (type === BarterParcelTypeEnum.counterparty || type === BarterParcelTypeEnum.received) {
                    barterParcel.invalidUsed = (Math.round(realAcreage - (rentedAcreage - unrentedAcreage)) < acreage)
                } else {
                    barterParcel.invalidNotUsed = (Math.round(rentedAcreage - unrentedAcreage) < acreage) || relevantValidityParams.parcelRentedRatio < 1
                    barterParcel.parcelNoFurtherRent =
                        //ze smlouvy
                        barterParcel.parcel.rentedByConcerned && !barterParcel.parcel.rentedByConcernedFurtherRent
                        ||
                        !barterParcel.parcel.barteredFurtherRent
                }

                if (type === BarterParcelTypeEnum.counterparty || type === BarterParcelTypeEnum.passed) {
                    /**
                     * If the parcel is not in the concerned subject's blocks then add the appropriate message to the content.
                     * Czech: atribut je součástí algoritmu spravovaného v Confluence: https://cleverfarm.atlassian.net/wiki/x/DgBg2Q
                     * V případě změny algoritmu je nutné aktualizovat i Confluence.
                     */
                    barterParcel.parcelOutsideLpisBlocks = (!barterParcel.parcel.endDate && !this.barterService.isAnyConcernedLpisUserIdIncluded(barterParcel.parcel.concernedUserIds, concernedSubjects))
                } else {
                    barterParcel.parcelOutsideLpisBlocks = (!barterParcel.parcel.endDate && !barterParcel.parcel.userIds.includes(barterCounterpartyId))
                }

                const parcelTypeConflicts = parcelConflicts[type]?.[barterParcel.parcelId]
                if (parcelTypeConflicts) {
                    parcelTypeConflicts.forEach(conflict => {
                        if (conflict.state === BARTER_STATE.proposal) {
                            barterParcel.parcelOnOtherProposalBarter = true
                        }
                        if (conflict.state === BARTER_STATE.valid) {
                            barterParcel.parcelOnOtherValidBarter = true
                        }
                    })
                }
                barterParcel.invalidOutdated = barterParcel.withoutAnyValidVersion
                barterParcel.parcelOutdated = !!barterParcel.parcel.endDate
            })
        })
    }

    private resetBarterParcelErrors(barterParcelGroups: any[], parcelId: number): void {
        barterParcelGroups.flatMap(barterParcelGroup => barterParcelGroup.parcels)
            .filter(barterParcel => barterParcel.parcelId === parcelId)
            .forEach(barterParcel => barterParcel.errors = [])
    }
}
